<template>
  <div class="page">
    <sui-segment class="content" padded attached emphasis="secondary">
      <sui-container>
        <h2 class="ui header">
          <i class="server grey icon" />
          <sui-header-content>Host {{host.name}}</sui-header-content>
        </h2>
        <sui-message warning icon="exclamation circle" v-if="buttons.connect">
          <p>Run this command on your host to connect it to D2C:</p>
          <sui-form>
            <sui-form-fields>
              <sui-form-field :width="16">
                <input class="connect-input" :value="connectCommand" readonly onfocus="this.select()" />
              </sui-form-field>
              <sui-form-field>
                <CopyIcon :value="connectCommand" />
              </sui-form-field>
            </sui-form-fields>
          </sui-form>
        </sui-message>
        <sui-message warning icon="clock" v-if="buttons.demo">
          This demo host will be destroyed <b>{{momentFromNow(host.demoExpires)}}</b><br>
          You will be able to create a new one.
        </sui-message>
        <sui-message warning icon="exclamation triangle" v-if="buttons.recreate">
          <sui-button icon="redo" label-position="left" color="orange" floated="right" :disabled="!actions.hostUpdate" data-goal="HOST_PAGE_WARNING_RECREATE" @click="recreate()">Recreate host</sui-button>
          The host hasn't been created properly.<br>
          Try again.
        </sui-message>
        <sui-message warning icon="exclamation triangle" v-if="buttons.resetup">
          <sui-button icon="redo" label-position="left" color="orange" floated="right" :disabled="!actions.hostUpdate" data-goal="HOST_PAGE_WARNING_RESETUP" @click="resetup()">Setup host</sui-button>
          The host hasn't been setup properly.<br>
          Try again.
        </sui-message>
        <sui-message warning icon="exclamation triangle" v-if="host.healthStatus && host.healthStatus.needReboot">
          <sui-button icon="refresh" label-position="left" color="orange" floated="right" :disabled="!actions.hostUpdate" data-goal="HOST_PAGE_WARNING_RESTART" v-show="buttons.restart" @click="restart()">Restart host</sui-button>
          The host needs to be restarted<br>
          for applying security updates.
        </sui-message>
        <sui-message icon="exclamation circle" dismissable error header="Error!" :content="error" v-if="error" @dismiss="error = null" />
        <sui-card-group class="state-cards doubling" :items-per-row="6">
          <HostProviderCard :host="host" />
          <HostLocationCard :host="host" />
          <HostStatusCard :host="host" />
          <HostHealthCard :host="host" />
          <HostSpecsCard :host="host" />
          <HostContainersCard :host="host" />
        </sui-card-group>
        <sui-segment>
          <div>
            <div style="float: right">
              <sui-button-group color="red">
                <sui-button class="labeled icon" icon="trash" :disabled="!actions.hostDestroy" data-goal="HOST_PAGE_DESTROY" @click="destroy()">Destroy</sui-button>
                <sui-dropdown class="icon" icon="dropdown" :disabled="!actions.hostDestroy" button>
                  <sui-dropdown-menu>
                    <sui-dropdown-item data-goal="HOST_PAGE_DESTROY_FORCE" @click="destroy(true)">
                      Force destroy <b>{{host.name}}</b><br>
                      <small class="dropdown-item-description">Remove the host from D2C, ignore all errors</small>
                    </sui-dropdown-item>
                  </sui-dropdown-menu>
                </sui-dropdown>
              </sui-button-group>
            </div>
            <sui-button color="blue" icon="cog" label-position="left" :disabled="inProgress" v-if="buttons.update" data-goal="HOST_PAGE_EDIT" @click="$modals.updateHost.open(host)">Edit</sui-button>
            <sui-button class="labeled icon" icon="terminal" color="black" :disabled="!isConnectable || !actions.hostTerminal" data-goal="HOST_PAGE_TERMINAL" v-show="buttons.terminal" @click="openTerminal()">
              Terminal
            </sui-button>
          </div>
        </sui-segment>
        <sui-tab class="tabs" :menu="{attached: 'top', color: 'blue', widths: config.debug ? 3 : 2}">
          <sui-tab-pane class="basic" title="System logs" icon="file alternate outline">
            <EntityLog :entity="host" />
          </sui-tab-pane>
          <sui-tab-pane title="Metrics" icon="chart bar">
            <Metrics :entity="host"  />
          </sui-tab-pane>
          <sui-tab-pane title="Debug" icon="user secret" v-if="config.debug">
            <codemirror class="commands" v-model="hostString" :options="{mode: 'javascript', lineNumbers: true, readOnly: true}" />
          </sui-tab-pane>
        </sui-tab>
        <br />
        <sui-segment basic>
          <ContainerCards :containers="containers" />
        </sui-segment>
      </sui-container>
    </sui-segment>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import ContainerCards from '@/components/cards/Containers'
  import HostContainersCard from '@/components/cards/HostContainers'
  import HostHealthCard from '@/components/cards/HostHealth'
  import HostLocationCard from '@/components/cards/HostLocation'
  import HostProviderCard from '@/components/cards/HostProvider'
  import HostSpecsCard from '@/components/cards/HostSpecs'
  import HostStatusCard from '@/components/cards/HostStatus'
  import Metrics from '@/components/Metrics'
  import EntityLog from '@/components/EntityLog'
  import store from '@/store'
  import utils from '@/utils'

  export default {
    name: 'HostView',
    components: {
      ContainerCards,
      Metrics,
      EntityLog,
      HostContainersCard,
      HostHealthCard,
      HostLocationCard,
      HostProviderCard,
      HostSpecsCard,
      HostStatusCard,
    },
    data() {
      return {
        error: null,
      }
    },
    computed: {
      ...mapGetters(['actionsById', 'account', 'config', 'hosts', 'keys']),
      actions() {
        return this.actionsById[this.host.id]
      },
      host() {
        const name = this.$route.params.host
        const host = this.hosts.find(entity => entity.id === name || entity.name === name)
        return host
      },
      hostString() {
        return JSON.stringify(this.host, null, 2)
      },
      containers() {
        return this.host._containers
      },
      isCreated() {
        return this.host._isCreated
      },
      isOnline() {
        return this.host._isOnline
      },
      isDeployable() {
        return this.host.deployable
      },
      isConnectable() {
        return this.host._isConnectable
      },
      inProgress() {
        return this.host._inProgress
      },
      hasIP() {
        return this.host._hasIP
      },
      connectCommand() {
        return `bash <(wget -qO- ${this.config.apiAddress}/getScript/${this.account.id}/${this.host.id}/add.sh)`
      },
      buttons() {
        const methods = this.host.constructor.methods
        return {
          connect: methods.connect && this.host._isConnecting,
          demo: this.host.demo,
          terminal: methods.terminal,
          recreate: methods.recreate && !this.isCreated && !this.inProgress,
          resetup: methods.resetup && this.isCreated && !this.inProgress && !this.isDeployable,
          restart: methods.restart && this.hasIP && this.isOnline,
          update: methods.update,
        }
      },
    },
    sockets: {
      delete(data) {
        if (data.target === 'host' && data.id === this.host.id) {
          this.$router.push({path: '/error/not-exist', query: {host: this.host.name}})
        }
      },
    },
    methods: {
      openTerminal() {
        this.$modals.terminal.open(this.host)
      },
      recreate() {
        this.host.recreate().catch(error => {
          this.error = error.message
        })
      },
      resetup() {
        this.host.setup().catch(error => {
          this.error = error.message
        })
      },
      restart() {
        return this.$modals.confirm.open({
          callback: () => {
            this.error = null
            this.host.restart().catch(error => {
              this.error = error.message
            })
          },
        })
      },
      destroy(force = false) {
        return this.$modals.confirm.open({
          callback: () => {
            this.error = null
            utils.reachGoal('HOST_PAGE_DESTROY_TRY')
            this.host.destroy(force).then(() => {
              utils.reachGoal('HOST_PAGE_DESTROY_SUCCESS')
              this.$router.push(this.config.homePath)
            }).catch(error => {
              utils.reachGoal('HOST_PAGE_DESTROY_FAIL')
              this.error = error.message
            })
          },
        })
      },
    },
    beforeRouteEnter(to, from, next) {
      const name = to.params.host
      const host = store.getters.hosts.find(entity => entity.id === name || entity.name === name)
      if (host) {
        next()
      } else {
        next({path: '/error/not-exist', query: {host: name}})
      }
    },
  }
</script>

<style scoped>
  .page { display: flex; flex-direction: column; }
  .page > .content { flex: 1; }
  .tabs >>> .menu { font-size: 1.2em!important; }
  .tabs >>> .basic.segment { margin-top: -1px!important; background: #fff!important; }
  .basic.segment { padding: 0!important; border: none!important; }
  .connect-input { margin-top: -.5em!important; background-color: rgba(0, 0, 0, .1)!important; font-size: .9em!important; font-family: monospace!important; opacity: 1!important; }
</style>
